export const BASE_HEIGHT = 5; // inches
export const LAYER_HEIGHT = 9; // inches
export const MAX_BAGS_PER_PALLET = 11;
export const BAGS_PER_LAYER = 2;
export const BAG_SPACE_POSITIONS_PER_PALLET = 12; // 2 bags × 6 layers; operational limit remains 11 bags
export const PALLET_LENGTH = 48; // inches
export const PALLET_WIDTH = 40; // inches
export const PALLET_STATIC_WEIGHT = 45; // lbs

export const BOX_LENGTH = 12; // inches
export const BOX_WIDTH = 12; // inches
export const BOX_HEIGHT = 9; // inches
export const BOX_STATIC_WEIGHT = 1; // lbs of packaging material per standard box
export const BOX_WEIGHT_THRESHOLD = 50; // items under 50 lbs are box products
export const BOX_PALLET_RATE_THRESHOLD = 10; // 10+ boxes use pallet/LTL rates
export const BOXES_PER_PALLET_LEVEL = 12; // 12 standard boxes fill one pallet level
export const MAX_BOX_LEVELS_PER_PALLET = 4; // 4 layers × 12 boxes = 48 boxes per pallet (temporary default)

export const SMALL_BOX_MAX_PRODUCT_WEIGHT = 5; // lbs; e.g. 2.2 lb Panama bags
export const SMALL_BOX_LENGTH = 8; // inches
export const SMALL_BOX_WIDTH = 8; // inches
export const SMALL_BOX_HEIGHT = 12; // inches
export const SMALL_BOX_TOTAL_WEIGHT = 2.5; // lbs including packaging

export type ShipmentClassification = "parcel" | "boxed_pallet" | "ltl";

export interface ShipmentWeightQuantityLike {
  weight?: number | null;
  quantity?: number | null;
}

export interface PalletizationSummary {
  totalPallets: number;
  totalHeight: number;
  heightPerPallet: number;
  totalWeight: number;
  totalVolumeCubicFt: number;
  density: number;
}

export interface ParcelShipmentSummary {
  totalPieces: number;
  weightPerPiece: number;
  totalWeight: number;
  length: number;
  width: number;
  height: number;
  density: number;
}

export interface MixedPalletizationSummary extends PalletizationSummary {
  totalUnits: number;
  bagUnits: number;
  boxUnits: number;
  bagPallets: number;
  boxPallets: number;
  shipmentClassification: ShipmentClassification;
}

interface NormalizedShipmentItem {
  weight: number;
  quantity: number;
}

interface BoxLayoutProfile {
  length: number;
  width: number;
  height: number;
  boxesPerPalletLevel: number;
  maxLevelsPerPallet: number;
}

function roundCurrency(value: number): number {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function normalizeItem(item: ShipmentWeightQuantityLike): NormalizedShipmentItem {
  const weight = Number(item.weight ?? 0);
  const quantity = Math.max(0, Number(item.quantity ?? 0) || 0);
  return {
    weight: Number.isFinite(weight) && weight > 0 ? weight : 0,
    quantity,
  };
}

function normalizeItems(items: ShipmentWeightQuantityLike[]): NormalizedShipmentItem[] {
  return items.map(normalizeItem).filter((item) => item.quantity > 0);
}

function getBoxLayoutProfile(items: ShipmentWeightQuantityLike[]): BoxLayoutProfile {
  const normalizedItems = normalizeItems(items).filter((item) => isBoxWeight(item.weight));
  const useSmallBoxProfile =
    normalizedItems.length > 0 &&
    normalizedItems.every((item) => item.weight <= SMALL_BOX_MAX_PRODUCT_WEIGHT);

  if (useSmallBoxProfile) {
    return {
      length: SMALL_BOX_LENGTH,
      width: SMALL_BOX_WIDTH,
      height: SMALL_BOX_HEIGHT,
      boxesPerPalletLevel: Math.floor(PALLET_LENGTH / SMALL_BOX_LENGTH) * Math.floor(PALLET_WIDTH / SMALL_BOX_WIDTH),
      maxLevelsPerPallet: MAX_BOX_LEVELS_PER_PALLET,
    };
  }

  return {
    length: BOX_LENGTH,
    width: BOX_WIDTH,
    height: BOX_HEIGHT,
    boxesPerPalletLevel: BOXES_PER_PALLET_LEVEL,
    maxLevelsPerPallet: MAX_BOX_LEVELS_PER_PALLET,
  };
}

function getBoxPackagingWeight(productWeight: number): number {
  if (productWeight > 0 && productWeight <= SMALL_BOX_MAX_PRODUCT_WEIGHT) {
    return Math.max(0, SMALL_BOX_TOTAL_WEIGHT - productWeight);
  }

  return BOX_STATIC_WEIGHT;
}

function calculateBoxPackagingWeight(items: ShipmentWeightQuantityLike[]): number {
  return normalizeItems(items)
    .filter((item) => isBoxWeight(item.weight))
    .reduce(
      (sum, item) => sum + getBoxPackagingWeight(item.weight) * item.quantity,
      0,
    );
}

function getEmptyPalletization(): PalletizationSummary {
  return {
    totalPallets: 0,
    totalHeight: 0,
    heightPerPallet: 0,
    totalWeight: 0,
    totalVolumeCubicFt: 0,
    density: 0,
  };
}

function calculateBoxPalletizationWithLayout(
  totalBoxes: number,
  totalProductWeight: number,
  totalPackagingWeight: number,
  layout: BoxLayoutProfile,
): PalletizationSummary & {
  totalLevels: number;
  levelsPerPallet: number;
} {
  if (totalBoxes <= 0) {
    return {
      totalLevels: 0,
      levelsPerPallet: 0,
      ...getEmptyPalletization(),
    };
  }

  const totalLevels = Math.ceil(totalBoxes / layout.boxesPerPalletLevel);
  const fullPallets = Math.floor(totalLevels / layout.maxLevelsPerPallet);
  const remainingLevels = totalLevels % layout.maxLevelsPerPallet;
  const totalPallets = fullPallets + (remainingLevels > 0 ? 1 : 0);
  const fullPalletHeight = BASE_HEIGHT + layout.maxLevelsPerPallet * layout.height;
  const partialPalletHeight = remainingLevels > 0
    ? BASE_HEIGHT + remainingLevels * layout.height
    : 0;
  const totalHeight = fullPallets * fullPalletHeight + partialPalletHeight;
  const heightPerPallet = totalPallets > 0 ? Math.ceil(totalHeight / totalPallets) : 0;
  const totalWeight =
    totalProductWeight +
    totalPackagingWeight +
    totalPallets * PALLET_STATIC_WEIGHT;

  const lengthFt = PALLET_LENGTH / 12;
  const widthFt = PALLET_WIDTH / 12;
  const heightFt = totalHeight / 12;
  const totalVolumeCubicFt = lengthFt * widthFt * heightFt;
  const density = totalVolumeCubicFt > 0 ? totalWeight / totalVolumeCubicFt : 0;

  return {
    totalLevels,
    levelsPerPallet: totalPallets > 0 ? Math.ceil(totalLevels / totalPallets) : 0,
    totalPallets,
    totalHeight,
    heightPerPallet,
    totalWeight: roundCurrency(totalWeight),
    totalVolumeCubicFt,
    density,
  };
}

export function getTotalShipmentQuantity(
  items: ShipmentWeightQuantityLike[],
): number {
  return items.reduce((sum, item) => {
    const quantity = Math.max(0, Number(item.quantity ?? 0) || 0);
    return sum + quantity;
  }, 0);
}

export function isBoxWeight(weight?: number | null): boolean {
  const normalizedWeight = Number(weight ?? 0);
  return Number.isFinite(normalizedWeight) && normalizedWeight > 0 && normalizedWeight < BOX_WEIGHT_THRESHOLD;
}

export function areAllItemsBoxes(items: ShipmentWeightQuantityLike[]): boolean {
  if (!items.length) return false;
  return items.every((item) => isBoxWeight(item.weight));
}

export function shouldUseParcelForBoxCount(totalBoxes: number): boolean {
  return totalBoxes > 0 && totalBoxes < BOX_PALLET_RATE_THRESHOLD;
}

export function classifyShipment(items: ShipmentWeightQuantityLike[]): ShipmentClassification {
  if (!items.length) {
    return "ltl";
  }

  const totalQuantity = getTotalShipmentQuantity(items);
  if (areAllItemsBoxes(items)) {
    return shouldUseParcelForBoxCount(totalQuantity) ? "parcel" : "boxed_pallet";
  }

  return "ltl";
}

export function calculateBagPalletization(
  totalUnits: number,
  totalProductWeight: number,
): PalletizationSummary {
  if (totalUnits <= 0) {
    return {
      totalPallets: 0,
      totalHeight: 0,
      heightPerPallet: 0,
      totalWeight: 0,
      totalVolumeCubicFt: 0,
      density: 0,
    };
  }

  const totalPallets = Math.ceil(totalUnits / MAX_BAGS_PER_PALLET);
  const layersPerPallet = Math.ceil(MAX_BAGS_PER_PALLET / BAGS_PER_LAYER);
  const fullPalletHeight = BASE_HEIGHT + layersPerPallet * LAYER_HEIGHT;
  const fullPallets = Math.floor(totalUnits / MAX_BAGS_PER_PALLET);
  const remainingBags = totalUnits % MAX_BAGS_PER_PALLET;

  let lastPalletHeight = 0;
  if (remainingBags > 0) {
    const lastPalletLayers = Math.ceil(remainingBags / BAGS_PER_LAYER);
    lastPalletHeight = BASE_HEIGHT + lastPalletLayers * LAYER_HEIGHT;
  }

  const totalHeight = fullPallets * fullPalletHeight + lastPalletHeight;
  const heightPerPallet = Math.ceil(totalHeight / totalPallets);
  const totalWeight = totalProductWeight + PALLET_STATIC_WEIGHT * totalPallets;

  const lengthFt = PALLET_LENGTH / 12;
  const widthFt = PALLET_WIDTH / 12;
  const heightFt = totalHeight / 12;
  const totalVolumeCubicFt = lengthFt * widthFt * heightFt;
  const density = totalVolumeCubicFt > 0 ? totalWeight / totalVolumeCubicFt : 0;

  return {
    totalPallets,
    totalHeight,
    heightPerPallet,
    totalWeight: roundCurrency(totalWeight),
    totalVolumeCubicFt,
    density,
  };
}

export function calculateBoxPalletization(
  totalBoxes: number,
  totalProductWeight: number,
): PalletizationSummary & {
  totalLevels: number;
  levelsPerPallet: number;
} {
  return calculateBoxPalletizationWithLayout(
    totalBoxes,
    totalProductWeight,
    totalBoxes * BOX_STATIC_WEIGHT,
    {
      length: BOX_LENGTH,
      width: BOX_WIDTH,
      height: BOX_HEIGHT,
      boxesPerPalletLevel: BOXES_PER_PALLET_LEVEL,
      maxLevelsPerPallet: MAX_BOX_LEVELS_PER_PALLET,
    },
  );
}

export function calculateParcelShipment(
  items: ShipmentWeightQuantityLike[],
): ParcelShipmentSummary {
  const normalizedItems = normalizeItems(items).filter((item) => isBoxWeight(item.weight));
  const totalPieces = getTotalShipmentQuantity(normalizedItems);

  if (totalPieces <= 0) {
    return {
      totalPieces: 0,
      weightPerPiece: 0,
      totalWeight: 0,
      length: BOX_LENGTH,
      width: BOX_WIDTH,
      height: BOX_HEIGHT,
      density: 0,
    };
  }

  const totalProductWeight = normalizedItems.reduce(
    (sum, item) => sum + item.weight * item.quantity,
    0,
  );
  const totalPackagingWeight = calculateBoxPackagingWeight(normalizedItems);
  const totalWeight = totalProductWeight + totalPackagingWeight;
  const layout = getBoxLayoutProfile(normalizedItems);
  const volumeCubicFt = (layout.length / 12) * (layout.width / 12) * (layout.height / 12);
  const weightPerPiece = totalWeight / totalPieces;

  return {
    totalPieces,
    weightPerPiece: roundCurrency(weightPerPiece),
    totalWeight: roundCurrency(totalWeight),
    length: layout.length,
    width: layout.width,
    height: layout.height,
    density: volumeCubicFt > 0 ? weightPerPiece / volumeCubicFt : 0,
  };
}

export function calculateBoxPalletizationForItems(
  items: ShipmentWeightQuantityLike[],
): PalletizationSummary & {
  totalLevels: number;
  levelsPerPallet: number;
} {
  const normalizedItems = normalizeItems(items).filter((item) => isBoxWeight(item.weight));
  const totalBoxes = getTotalShipmentQuantity(normalizedItems);
  const totalProductWeight = normalizedItems.reduce(
    (sum, item) => sum + item.weight * item.quantity,
    0,
  );
  const totalPackagingWeight = calculateBoxPackagingWeight(normalizedItems);

  return calculateBoxPalletizationWithLayout(
    totalBoxes,
    totalProductWeight,
    totalPackagingWeight,
    getBoxLayoutProfile(normalizedItems),
  );
}

export function calculatePalletizedShipment(
  items: ShipmentWeightQuantityLike[],
): MixedPalletizationSummary {
  const normalizedItems = normalizeItems(items);
  const totalUnits = getTotalShipmentQuantity(normalizedItems);
  const shipmentClassification = classifyShipment(normalizedItems);

  if (totalUnits <= 0) {
    return {
      ...getEmptyPalletization(),
      totalUnits: 0,
      bagUnits: 0,
      boxUnits: 0,
      bagPallets: 0,
      boxPallets: 0,
      shipmentClassification,
    };
  }

  const boxItems = normalizedItems.filter((item) => isBoxWeight(item.weight));
  const bagItems = normalizedItems.filter((item) => !isBoxWeight(item.weight));
  const boxUnits = getTotalShipmentQuantity(boxItems);
  const bagUnits = getTotalShipmentQuantity(bagItems);

  const bagPalletization = bagUnits > 0
    ? calculateBagPalletization(
      bagUnits,
      bagItems.reduce((sum, item) => sum + item.weight * item.quantity, 0),
    )
    : getEmptyPalletization();

  const boxPalletization = boxUnits > 0
    ? calculateBoxPalletizationForItems(boxItems)
    : getEmptyPalletization();

  const boxLayout = getBoxLayoutProfile(boxItems);
  const boxCapacityPerPallet =
    boxLayout.boxesPerPalletLevel * boxLayout.maxLevelsPerPallet;
  const boxCapacityUsedByEachBag =
    boxCapacityPerPallet / BAG_SPACE_POSITIONS_PER_PALLET;
  const capacityPallets = Math.ceil(
    (bagUnits * boxCapacityUsedByEachBag + boxUnits) / boxCapacityPerPallet,
  );
  const totalPallets = Math.max(
    bagPalletization.totalPallets,
    capacityPallets,
  );
  const separatelyCalculatedPallets =
    bagPalletization.totalPallets + boxPalletization.totalPallets;
  const sharedPallets = Math.max(0, separatelyCalculatedPallets - totalPallets);
  const totalHeight =
    bagPalletization.totalHeight +
    boxPalletization.totalHeight -
    sharedPallets * BASE_HEIGHT;
  const totalWeight =
    bagPalletization.totalWeight +
    boxPalletization.totalWeight -
    sharedPallets * PALLET_STATIC_WEIGHT;
  const palletAreaSquareFt = (PALLET_LENGTH / 12) * (PALLET_WIDTH / 12);
  const totalVolumeCubicFt = palletAreaSquareFt * (totalHeight / 12);
  const density = totalVolumeCubicFt > 0 ? totalWeight / totalVolumeCubicFt : 0;

  return {
    totalUnits,
    bagUnits,
    boxUnits,
    bagPallets: bagPalletization.totalPallets,
    boxPallets: Math.max(0, totalPallets - bagPalletization.totalPallets),
    totalPallets,
    totalHeight,
    heightPerPallet: totalPallets > 0 ? Math.ceil(totalHeight / totalPallets) : 0,
    totalWeight: roundCurrency(totalWeight),
    totalVolumeCubicFt,
    density,
    shipmentClassification,
  };
}
